import { emptyRow, type AnalyteKind, type AnalyteRow } from './assayDefinition';
import type { AssayMetadataValues } from './assayMetadata';

export interface KindHelp {
  what: string;
  example: string;
}

export const KIND_HELP: Record<AnalyteKind, KindHelp> = {
  result: { what: 'A reported value typed by the operator or read from the instrument.', example: 'pH, Cu (g/L), TOC' },
  input: { what: 'A bench reading a formula consumes. Typed on a manual run; not a result on its own.', example: 'titrant volume, sample mass, crucible mass' },
  derived: { what: 'Computed from inputs and constants by its formula. Never typed.', example: 'acid g/L from titrant volume, insolubles mass from two weighings' },
  constant: { what: 'A number fixed by the method that a formula reads by key. Never typed, never a column.', example: 'final volume 100 mL, titrant factor 35.45' },
  qc: { what: 'Calibration or control data, kept with the test but not a sample result.', example: 'blank residue, control standard reading' },
  meta: { what: 'Bookkeeping text that goes with the test. Never shown as a value, never charted.', example: 'observations, instrument id, method note' },
};

export const COLUMN_HELP = {
  name: 'What the lab calls the value, as shown on pages and reports: "Sample mass", "Acid-insoluble matter".',
  key: 'The worksheet key the value is stored under. Follows the name as snake_case; formulas read keys. Once tests exist, a key is a contract: rename the analyte, not the key.',
  kind: 'What role the value plays: result, input, derived, constant, qc or meta. See the legend below the table.',
  unit: 'The unit the value is recorded in: g, mL, %, g/L. Leave blank for a unitless value such as pH.',
  decimals: 'How many decimal places pages show and derived values are rounded to.',
  headline: 'The one value Routine Sample Management shows for a test when it can show only one. Usually one; if several are flagged, the first with a value is shown.',
  display: 'Shown on result pages. When any row is flagged, only the flagged rows are shown; otherwise every result and derived value is.',
  defaultValue: 'input or result: the starting value offered to the operator (a usual sample size). constant: its fixed value.',
  formula: 'Arithmetic over keys: + - * / and parentheses, e.g. (final_mass - initial_mass) / sample_mass * 100. Constants are read by key. A formula that reads another derived value must sit below it in the table.',
  workedExample: 'Numbers from the SOP or a real run: type each input and the result the method gives, and the formula is checked against it before you can save.',
  optional: 'The test may complete while this derived value is still blank.',
} as const;

const row = (partial: Partial<AnalyteRow>): AnalyteRow => ({ ...emptyRow(), ...partial, keyEdited: true });

// SF HQ lab SOP STD-AIM-001 (modified GB/T 11064.11-2013).
export function exampleAssay(): { meta: AssayMetadataValues; rows: AnalyteRow[] } {
  return {
    meta: {
      title: 'Acid Insoluble Matter in Lithium Carbonate',
      sampleTypes: 'lithium carbonate',
      capture: 'upload',
      method: 'Gravimetry after HCl dissolution (modified GB/T 11064.11-2013)',
      description: 'Residue retained by a sintered-glass crucible after HCl dissolution, hot-water washing and drying to constant mass, as a percentage of the 230 °C pretreated sample mass. Two determinations per sample; report the mean.',
      sopUrl: '',
    },
    rows: [
      row({ name: 'Sample mass', key: 'sample_mass', kind: 'input', unit: 'g', decimals: '2', default: '50' }),
      row({ name: 'Empty crucible mass', key: 'crucible_initial_mass', kind: 'input', unit: 'g', decimals: '4' }),
      row({ name: 'Crucible plus residue mass', key: 'crucible_final_mass', kind: 'input', unit: 'g', decimals: '4' }),
      row({
        name: 'Insolubles mass', key: 'insolubles_mass', kind: 'derived', unit: 'g', decimals: '4', display: true,
        formula: 'crucible_final_mass - crucible_initial_mass',
        example: { crucible_final_mass: '32.4617', crucible_initial_mass: '32.4567' }, expected: '0.0050',
      }),
      row({
        name: 'Acid-insoluble matter', key: 'acid_insoluble_matter', kind: 'derived', unit: '%', decimals: '4', headline: true, display: true,
        formula: 'insolubles_mass / sample_mass * 100',
        example: { insolubles_mass: '0.0050', sample_mass: '50' }, expected: '0.0100',
      }),
      row({ name: 'Observations', key: 'observations', kind: 'meta' }),
    ],
  };
}

export const EXAMPLE_UPLOAD_COLUMNS: Array<{ column: string; key: string | null }> = [
  { column: 'SID', key: 'sample id' },
  { column: 'Li2CO3 Mass (g)', key: 'sample_mass' },
  { column: 'Initial Crucible Filter Mass (g)', key: 'crucible_initial_mass' },
  { column: 'Final Crucible Filter Mass (g)', key: 'crucible_final_mass' },
  { column: 'Insolubles Mass (g)', key: null },
  { column: 'Wt% Insolubles (%)', key: null },
  { column: 'Observations', key: 'observations' },
];
